import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import StoreHeader from "../../components/layout/StoreHeader";
import StoreFooter from "../../components/layout/StoreFooter";
import { Button } from "../../components/ui/button";
import { formatAud } from "../../lib/api";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { items, subtotal, updateQty, removeItem } = useCart();
  const navigate = useNavigate();

  return (
    <div className="bg-white min-h-screen" data-testid="cart-page">
      <StoreHeader />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <div className="text-red-600 font-mono text-xs uppercase tracking-[0.3em] mb-2">Checkout · 01</div>
        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight text-zinc-950 mb-8">
          Your cart
        </h1>

        {items.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-16 text-center" data-testid="cart-empty">
            <ShoppingBag className="h-8 w-8 mx-auto text-zinc-400" />
            <div className="mt-4 font-heading text-lg font-bold uppercase">Cart is empty</div>
            <p className="mt-2 text-sm text-zinc-500">Browse the catalogue and add parts to get started.</p>
            <Link to="/shop" className="inline-flex mt-6 h-11 px-6 bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-wide font-bold text-sm items-center rounded-sm">
              Shop parts
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 border border-zinc-200 rounded-sm">
              {items.map((i) => (
                <div key={i.product_id} className="flex gap-4 p-4 border-b last:border-b-0 border-zinc-200" data-testid={`cart-row-${i.sku}`}>
                  <div className="w-24 h-24 bg-zinc-100 shrink-0 overflow-hidden">
                    {i.image_url && <img src={i.image_url} alt={i.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{i.sku}</div>
                    <div className="font-heading font-bold text-sm line-clamp-2">{i.name}</div>
                    <div className="mt-2 text-sm text-zinc-600">{formatAud(i.price)} ea</div>
                  </div>
                  <div className="flex flex-col items-end gap-2 justify-between">
                    <button onClick={() => removeItem(i.product_id)} className="text-zinc-400 hover:text-red-600" data-testid={`cart-remove-${i.sku}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <div className="flex border border-zinc-300 rounded-sm">
                      <button onClick={() => updateQty(i.product_id, i.qty - 1)} className="w-8 hover:bg-zinc-100" data-testid={`cart-decrement-${i.sku}`}><Minus className="h-3 w-3 mx-auto" /></button>
                      <div className="w-8 flex items-center justify-center text-sm font-bold" data-testid={`cart-qty-${i.sku}`}>{i.qty}</div>
                      <button onClick={() => updateQty(i.product_id, i.qty + 1)} className="w-8 hover:bg-zinc-100" data-testid={`cart-increment-${i.sku}`}><Plus className="h-3 w-3 mx-auto" /></button>
                    </div>
                    <div className="font-heading font-black text-sm">{formatAud(i.price * i.qty)}</div>
                  </div>
                </div>
              ))}
            </div>

            <aside className="border border-zinc-200 rounded-sm p-6 h-fit sticky top-28" data-testid="cart-summary">
              <div className="font-heading text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-4">Order summary</div>
              <div className="flex justify-between text-sm py-2">
                <span className="text-zinc-600">Subtotal <span className="text-xs text-zinc-400">(incl. GST)</span></span>
                <span className="font-bold">{formatAud(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm py-2 text-zinc-400">
                <span>Shipping</span>
                <span className="font-mono text-xs uppercase tracking-wider">at checkout</span>
              </div>
              <div className="border-t border-zinc-200 mt-2 pt-3 flex justify-between items-baseline">
                <span className="font-heading uppercase text-sm">Estimated total</span>
                <span className="font-heading font-black text-2xl">{formatAud(subtotal)}</span>
              </div>
              <Button
                onClick={() => navigate("/checkout")}
                className="w-full mt-6 h-12 rounded-sm bg-red-600 hover:bg-red-700 font-heading font-bold uppercase tracking-wide"
                data-testid="cart-checkout-btn"
              >
                Proceed to checkout
              </Button>
              <Link to="/shop" className="block mt-3 text-center text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900">
                Continue shopping
              </Link>
            </aside>
          </div>
        )}
      </div>
      <StoreFooter />
    </div>
  );
}